// src/messagerie/pages/MessagingDoctor.jsx
"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { axiosGateway } from "../../api/axios";
import { MESSAGES_PATHS } from "../../api/endpoints";

import AppLayoutDoctor from "../../navigation/Layout/LayoutDoctor";
import {
  MessagingProvider,
  useMessaging as useMessagingContext,
} from "../context/MessagingContext";
import { SocketProvider } from "../context/SocketContext";
import ProfileSidebar from "../components/ProfileSidebar/ProfileSidebar";
import ContactsTabs from "../components/ContactsList/DoctorsContactsList";
import ChatWindow from "../components/ChatWindow/ChatWindow";
import { getCurrentDoctor } from "../../authDoctor/redux/thunks/doctorsThunks";
import { restoreSessionFromStorage } from "../../authDoctor/redux/slices/doctorsSlice";

import "./Messaging.css";

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const normId = (v) => {
  if (v == null) return null;
  let s = String(v).trim();
  if (!s) return null;
  if (s.startsWith("DR_") || s.startsWith("dr_")) s = s.slice(3);
  if (s.startsWith("PAT_") || s.startsWith("pat_")) s = s.slice(4);
  s = s.trim();
  if (!s || s.toLowerCase() === "nan" || s.toLowerCase() === "undefined" || s.toLowerCase() === "null")
    return null;
  return s;
};

const buildMinimalPatientContact = (patientId) => {
  const id = normId(patientId);
  if (!id) return null;
  return {
    id,
    userId: id,
    peerId: id,
    name: "Patient",
    fullName: "Patient",
    avatar: "https://cdn-icons-png.flaticon.com/512/149/149071.png",
    photo: "https://cdn-icons-png.flaticon.com/512/149/149071.png",
    meta: {
      type: "patient",
      role: "patient",
      patientId: id,
      registerId: id,
      displayName: "Patient",
    },
  };
};

const buildMinimalDoctorContact = (doctorId, conversationId) => {
  const id = normId(doctorId);
  if (!id) return null;
  return {
    id,
    userId: id,
    peerId: id,
    name: "Doctor",
    fullName: "Doctor",
    avatar: "https://cdn-icons-png.flaticon.com/512/149/149071.png",
    photo: "https://cdn-icons-png.flaticon.com/512/149/149071.png",
    meta: {
      type: "doctor",
      role: "doctor",
      doctorId: id,
      registerId: id,
      conversationId: conversationId || undefined,
      displayName: "Doctor",
    },
  };
};

/* ─────────────────────────────────────────────
   Hook mobile
───────────────────────────────────────────── */
function useIsMobile(maxWidth = 768) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= maxWidth;
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= maxWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [maxWidth]);

  return isMobile;
}

/* ─────────────────────────────────────────────
   Corps de la page
───────────────────────────────────────────── */
function MessagingBody({ role, userId, isMobile }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    selectedContact,
    profileCollapsed,
    selectContact,
    toggleProfile,
    sharedMessages,
  } = useMessagingContext();

  const [tab, setTab] = useState("patients");
  const [routeContact, setRouteContact] = useState(null);
  const [initialDoctorError, setInitialDoctorError] = useState("");
  const openedDoctorRef = useRef(null);
  const handleTabChange = (next) => {
    if (next === tab) return;
    selectContact(null);
    setTab(next);
  };

  /* Sélectionner le contact depuis l'URL, sans remplacer la session active. */
  useEffect(() => {
    const sp = new URLSearchParams(location.search || "");
    const patientId = normId(sp.get("patientId"));
    const doctorId = normId(sp.get("doctorId"));
    const conversationId = sp.get("conversationId");
    const targetId = patientId || doctorId;
    if (!targetId) return;

    const current = normId(
      selectedContact?.id ||
        selectedContact?.userId ||
        selectedContact?.meta?.patientId ||
        selectedContact?.meta?.doctorId
    );
    if (current && current === targetId) {
      setTab(patientId ? "patients" : "doctors");
      const contact =
        !patientId &&
        conversationId &&
        selectedContact?.meta?.conversationId !== conversationId
          ? {
              ...selectedContact,
              meta: { ...selectedContact.meta, conversationId },
            }
          : selectedContact;
      setRouteContact(contact);
      if (contact !== selectedContact) selectContact(contact);
      return;
    }

    let contact = null;
    try {
      const raw = sessionStorage.getItem("pendingSelectedContact");
      if (raw) {
        const parsed = JSON.parse(raw);
        const pendingId = patientId
          ? normId(parsed?.meta?.patientId) || normId(parsed?.id) || normId(parsed?.userId)
          : normId(parsed?.meta?.doctorId) || normId(parsed?.id) || normId(parsed?.userId);
        if (pendingId && pendingId === targetId) contact = parsed;
      }
    } catch {}

    if (!contact) {
      contact = patientId
        ? buildMinimalPatientContact(patientId)
        : buildMinimalDoctorContact(doctorId, conversationId);
    }
    if (!patientId && conversationId && contact) {
      contact = {
        ...contact,
        meta: { ...contact.meta, conversationId },
      };
    }
    if (contact) {
      if (!patientId) setTab("doctors");
      setRouteContact(contact);
      selectContact(contact);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const requestedDoctor = location.state?.selectedDoctor;
    const requestedDoctorId =
      requestedDoctor?.doctorId ||
      requestedDoctor?.id ||
      requestedDoctor?._id ||
      requestedDoctor?.userId ||
      requestedDoctor?.pgId ||
      requestedDoctor?.externalId ||
      requestedDoctor?.raw?.doctorId ||
      requestedDoctor?.raw?.id ||
      requestedDoctor?.raw?._id ||
      requestedDoctor?.raw?.userId ||
      requestedDoctor?.raw?.pgId ||
      requestedDoctor?.raw?.externalId;
    const doctorId = normId(requestedDoctorId);
    if (!requestedDoctor || role !== "doctor") return;
    if (!doctorId) {
      setInitialDoctorError(
        t("messaging.errors.missingDoctorId", {
          defaultValue: "Unable to open the conversation: doctor identifier is missing",
        })
      );
      return;
    }
    if (openedDoctorRef.current === doctorId) return;

    openedDoctorRef.current = doctorId;
    setTab("doctors");
    setInitialDoctorError("");
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });

    axiosGateway
      .post(
        MESSAGES_PATHS.CONV_DOCTOR_TO_DOCTOR,
        { doctorId },
        {
          withCredentials: true,
          allowedRequestFields: ["doctorId"],
        }
      )
      .then((res) => {
        const conversation = res.data?.conversation || res.data;
        const conversationId = conversation?.id || conversation?._id;
        if (!conversationId) throw new Error("Unable to create doctor conversation");

        const name =
          requestedDoctor.fullName ||
          requestedDoctor.name ||
          `Dr. ${doctorId.slice(-6)}`;
        selectContact({
          id: doctorId,
          userId: doctorId,
          peerId: doctorId,
          name,
          fullName: name,
          email: requestedDoctor.email,
          specialty: requestedDoctor.specialty,
          meta: {
            type: "doctor",
            role: "doctor",
            conversationId,
            displayName: name,
          },
        });
      })
      .catch((err) => {
        setInitialDoctorError(
          err?.response?.data?.message ||
            err.message ||
            t("messaging.errors.loadConversation", {
              defaultValue: "Unable to open this conversation",
            })
        );
      });
  }, [location.pathname, location.search, location.state, navigate, role, selectContact, t]);

  const mobileTitle =
    selectedContact?.fullName ||
    selectedContact?.name ||
    selectedContact?.email ||
    t("messaging.state.conversation");

  const handleBackToList = () => {
    selectContact(null);
    navigate("/messages", { replace: true });
  };

  /* ── RENDU MOBILE ── */
  if (isMobile) {
    return (
      <main className="msg-mobile-page">
        {!selectedContact ? (
          /* Liste des contacts */
          <section className="msg-mobile-list">
            <ContactsTabs
              tab={tab}
              onTabChange={handleTabChange}
              meId={userId}
              onSelectContact={selectContact}
              selectedContactId={null}
              additionalContacts={routeContact?.meta?.role === "doctor" ? [routeContact] : []}
            />
            {initialDoctorError && <div className="dcl-empty dcl-error" role="alert">{initialDoctorError}</div>}
          </section>
        ) : (
          /* Fenêtre de conversation */
          <section className="msg-mobile-chat">
            <div className="msg-mobile-chat-header">
              <button
                type="button"
                className="msg-mobile-chat-back"
                onClick={handleBackToList}
                aria-label={t("messaging.actions.back")}
              >
                ←
              </button>
              <div className="msg-mobile-chat-title">{mobileTitle}</div>
            </div>

            <div className="msg-mobile-chat-body">
              <ChatWindow
                selectedContact={selectedContact}
                isDoctor={role === "doctor"}
                meOverride={userId}
                useSocket={true}
              />
            </div>
          </section>
        )}
      </main>
    );
  }

  /* ── RENDU DESKTOP ── */
  return (
    <main className={`msg-content ${selectedContact ? "msg--mobile-chat-open" : ""}`}>
      <div className={`msg-3col ${profileCollapsed ? "no-profile" : "with-profile"}`}>
        {/* Colonne contacts */}
        <div className="msg-contacts">
          <div className="msg-contacts__scroll">
            <ContactsTabs
              tab={tab}
              onTabChange={handleTabChange}
              meId={userId}
              onSelectContact={selectContact}
              selectedContactId={selectedContact?.id || selectedContact?.userId}
              additionalContacts={routeContact?.meta?.role === "doctor" ? [routeContact] : []}
            />
          </div>
        </div>

        {/* Colonne chat */}
        <div className="msg-chat">
          {!selectedContact ? (
            <div className="msg-empty">
              <div className="msg-empty__inner">
                {initialDoctorError ? (
                  <p role="alert" style={{ margin: 0, color: "#b91c1c" }}>{initialDoctorError}</p>
                ) : (
                  <>
                    <h3 style={{ margin: 0 }}>{t("messaging.empty.title")}</h3>
                    <p style={{ marginTop: 8, color: "#64748b" }}>{t("messaging.empty.subtitle")}</p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <ChatWindow
              selectedContact={selectedContact}
              isDoctor={role === "doctor"}
              meOverride={userId}
              useSocket={true}
            />
          )}
        </div>

        {/* Colonne profil */}
        {!profileCollapsed && selectedContact && (
          <div className="msg-profile">
            <div className="msg-profile__scroll">
              <ProfileSidebar
                contact={selectedContact}
                messages={sharedMessages}
                collapsed={profileCollapsed}
                onToggle={toggleProfile}
              />
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

/* ─────────────────────────────────────────────
   Composant racine
   → isMobile décidé ici pour conditionner AppLayoutDoctor
───────────────────────────────────────────── */
export default function MessagingDoctor() {
  const dispatch = useDispatch();
  const isMobile = useIsMobile(768); // ← une seule source de vérité

  const doctorState = useSelector((state) => state.doctor);
  const currentUser = doctorState?.user || doctorState?.currentUser || null;
  const loading = doctorState?.loading || false;

  const doctorId = currentUser?.id || currentUser?.doctorId || currentUser?.userId || null;
  const token =
    doctorState?.accessToken ||
    Cookies.get("doctorAccessToken") ||
    Cookies.get("doctor_token") ||
    Cookies.get("doctorToken");

  const [initDone, setInitDone] = useState(false);

  useEffect(() => {
    dispatch(restoreSessionFromStorage());
  }, [dispatch]);

  useEffect(() => {
    if (initDone) return;
    if (currentUser) { setInitDone(true); return; }
    if (token && !loading) dispatch(getCurrentDoctor());
  }, [dispatch, currentUser, token, loading, initDone]);

  useEffect(() => {
    if (initDone) return;
    if (currentUser) {
      setInitDone(true);
    } else if (!loading && !currentUser && token) {
      const t = setTimeout(() => setInitDone(true), 500);
      return () => clearTimeout(t);
    } else if (!token) {
      setInitDone(true);
    }
  }, [currentUser, loading, initDone, token]);

  const ready = !!(doctorId && token && initDone);

  /* État de chargement — sur mobile aussi sans AppLayoutDoctor */
  if (!ready) {
    if (isMobile) {
      return (
        <div style={{
          position: "fixed", inset: 0, display: "flex",
          alignItems: "center", justifyContent: "center",
          background: "#fff", flexDirection: "column", gap: 8
        }}>
          <div style={{ fontSize: 14, color: "#64748b" }}>Chargement...</div>
          <small style={{ color: "#94a3b8", fontSize: 12 }}>
            {!initDone && "Initialisation... "}
            {initDone && !doctorId && "Aucun ID médecin trouvé. "}
            {!token && "Token manquant. "}
          </small>
        </div>
      );
    }
    return (
      <AppLayoutDoctor>
        <div style={{ padding: 24 }}>
          Loading...
          <br />
          <small style={{ color: "#888" }}>
            {!initDone && "Initializing... "}
            {initDone && !doctorId && "No doctor ID found. "}
            {!token && "No token. "}
          </small>
        </div>
      </AppLayoutDoctor>
    );
  }

  return (
    <SocketProvider userId={doctorId} token={token}>
      <MessagingProvider>
        {isMobile ? (
          /*
           * Sur mobile : MessagingBody SANS AppLayoutDoctor
           * pour éviter que le sidebar écrase le mode plein-écran messenger.
           */
          <MessagingBody role="doctor" userId={doctorId} isMobile={true} />
        ) : (
          <AppLayoutDoctor>
            <MessagingBody role="doctor" userId={doctorId} isMobile={false} />
          </AppLayoutDoctor>
        )}
      </MessagingProvider>
    </SocketProvider>
  );
}